import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SocialSharePanel = ({ 
  analysisData,
  bikeImage,
  onShare = () => {},
  className = ""
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const shareData = {
    title: `My ${analysisData?.bikeModel || 'Bike'} Analysis Results`,
    text: `Check out my bike analysis! Found ${analysisData?.upgradeableComponents || 6} potential upgrades with ${analysisData?.estimatedPerformanceGain || 23}% performance improvement. 🚴‍♂️`,
    url: window.location?.href,
    hashtags: ['BikeUpgrade', 'Cycling', 'BikeAnalysis', 'Performance']
  };

  const socialPlatforms = [
    {
      name: 'Twitter',
      icon: 'Twitter',
      color: '#1DA1F2',
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareData?.text)}&url=${encodeURIComponent(shareData?.url)}&hashtags=${shareData?.hashtags?.join(',')}`
    },
    {
      name: 'Facebook',
      icon: 'Facebook',
      color: '#4267B2',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareData?.url)}&quote=${encodeURIComponent(shareData?.text)}`
    },
    {
      name: 'LinkedIn',
      icon: 'Linkedin',
      color: '#0077B5',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareData?.url)}&title=${encodeURIComponent(shareData?.title)}&summary=${encodeURIComponent(shareData?.text)}`
    },
    {
      name: 'Reddit',
      icon: 'MessageCircle',
      color: '#FF4500',
      url: `https://reddit.com/submit?url=${encodeURIComponent(shareData?.url)}&title=${encodeURIComponent(shareData?.title)}`
    }
  ];

  const handleSocialShare = (platform) => {
    window.open(platform?.url, '_blank', 'width=600,height=400');
    onShare('social', platform?.name);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard?.writeText(shareData?.url);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
      onShare('copy', 'link');
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareData?.title,
          text: shareData?.text,
          url: shareData?.url
        });
        onShare('native', 'system');
      } catch (err) {
        console.error('Error sharing:', err);
      }
    }
  };

  const handleDownloadImage = () => {
    // Create a canvas to combine bike image with analysis overlay
    const canvas = document.createElement('canvas');
    const ctx = canvas?.getContext('2d');
    
    // This would typically involve more complex image processing
    // For now, we'll just trigger a download action
    onShare('download', 'image');
  };

  return (
    <div className={`bg-card border border-border rounded-lg overflow-hidden ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Share2" size={20} color="var(--color-accent)" />
            <h3 className="font-semibold text-foreground">Share Your Analysis</h3>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="lg:hidden p-2 hover:bg-muted rounded-full transition-colors interactive-scale"
            aria-label="Toggle share options"
          >
            <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={20} />
          </button>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Show off your bike setup and upgrade plans
        </p>
      </div>
      {/* Share Options */}
      <div className={`p-4 space-y-4 ${isExpanded ? 'block' : 'hidden lg:block'}`}>
        {/* Quick Actions */}
        <div className="flex flex-wrap gap-2">
          {/* Native Share (Mobile) */}
          {navigator.share && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleNativeShare}
              iconName="Share"
              iconPosition="left"
              className="flex-1 sm:flex-none"
            >
              Share
            </Button>
          )}

          {/* Copy Link */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopyLink}
            iconName={copySuccess ? "Check" : "Copy"}
            iconPosition="left"
            className="flex-1 sm:flex-none"
          >
            {copySuccess ? 'Copied!' : 'Copy Link'}
          </Button>

          {/* Download Image */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadImage}
            iconName="Download"
            iconPosition="left"
            className="flex-1 sm:flex-none"
          >
            Save Image
          </Button>
        </div>

        {/* Social Platforms */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Share on Social Media</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {socialPlatforms?.map((platform) => (
              <button
                key={platform?.name}
                onClick={() => handleSocialShare(platform)}
                className="flex items-center justify-center space-x-2 p-3 border border-border rounded-md hover:bg-muted transition-colors interactive-scale"
              >
                <Icon name={platform?.icon} size={18} color={platform?.color} />
                <span className="text-sm font-medium text-foreground">{platform?.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Share Preview */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Share Preview</h4>
          <div className="p-3 border border-border rounded-md bg-muted/30">
            <div className="flex items-start space-x-3">
              <div className="w-12 h-12 bg-accent/10 rounded-md flex items-center justify-center">
                <Icon name="Bike" size={20} color="var(--color-accent)" />
              </div>
              <div className="flex-1">
                <h5 className="text-sm font-medium text-foreground">{shareData?.title}</h5>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                  {shareData?.text}
                </p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {shareData?.hashtags?.map((tag) => (
                    <span key={tag} className="text-xs text-accent">#{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Share Stats */}
        <div className="pt-4 border-t border-border">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Share your cycling journey</span>
            <div className="flex items-center space-x-4 text-muted-foreground">
              <div className="flex items-center space-x-1">
                <Icon name="Users" size={14} />
                <span>Connect with cyclists</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialSharePanel;